import { Box, Link as MuiLink, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  IconArrowLeft,
  IconHeadset,
  IconShieldCheck,
  IconUserCheck,
} from '@tabler/icons-react';
import { Link, useNavigate } from 'react-router-dom';
import { Heading, Text } from '../components/atomic';
import SEO from '../components/SEO';
import SecureEmail from '../components/SecureEmail';
import { Logo } from '../components/ui';

/**
 * Forgot Password / Contact Support page for EllaFin
 *
 * Since there is no self-service password reset system,
 * this page directs users to contact support.
 *
 * @returns {JSX.Element} Contact Support page component
 */
function ForgotPassword() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const handleLogoClick = () => {
    navigate('/');
  };

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
      }}
    >
      <SEO
        title="Contact Support - EllaFin"
        description="Need help with your EllaFin account? Contact our support team."
        path="/forgot-password"
        noIndex={true}
        keywords="contact support, account help, password reset"
      />

      {/* Brand Panel - Left Side */}
      <Box
        sx={{
          width: { xs: '100%', md: '45%' },
          minHeight: { xs: '280px', md: '100vh' },
          backgroundColor: theme.custom?.footerBackground || '#13152b',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: { xs: 4, md: 6 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background image with overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${process.env.PUBLIC_URL}/images/AdobeStock_1890471615.jpeg)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.15,
          }}
        />

        {/* Green accent bar at top */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            backgroundColor: theme.palette.secondary.main,
          }}
        />

        {/* Brand Content */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            textAlign: 'center',
            maxWidth: '420px',
          }}
        >
          <Logo
            onClick={handleLogoClick}
            sx={{
              height: { xs: '50px', md: '65px' },
              mb: 5,
              filter: 'brightness(0) invert(1)',
              cursor: 'pointer',
            }}
            alt="EllaFin™ Logo - Click to return to homepage"
          />

          <Heading
            variant="h1"
            sx={{
              color: '#ffffff',
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.75rem', lg: '3.25rem' },
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              mb: 2,
            }}
          >
            We're Here to Help
          </Heading>

          <Text
            sx={{
              color: theme.palette.secondary.main,
              fontSize: { xs: '1.1rem', md: '1.25rem' },
              fontStyle: 'italic',
              mb: 5,
              lineHeight: 1.6,
            }}
          >
            Our support team is ready to assist you
          </Text>

          {/* Trust Indicators */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              flexWrap: 'wrap',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              pt: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.9rem',
              }}
            >
              <IconShieldCheck
                size={20}
                stroke={1.5}
                color={theme.palette.secondary.main}
              />
              <span>SOC 2 Compliant</span>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.9rem',
              }}
            >
              <IconUserCheck
                size={20}
                stroke={1.5}
                color={theme.palette.secondary.main}
              />
              <span>Enterprise Security</span>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Content Panel - Right Side */}
      <Box
        sx={{
          width: { xs: '100%', md: '55%' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: { xs: 3, sm: 4, md: 6 },
          backgroundColor: '#ffffff',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: '440px',
          }}
        >
          {/* Mobile Logo */}
          {isMobile && (
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Logo
                onClick={handleLogoClick}
                sx={{ height: '40px' }}
                alt="EllaFin™ Logo - Click to return to homepage"
              />
            </Box>
          )}

          {/* Icon + heading */}
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: theme.palette.primary.main,
                mb: 2,
              }}
            >
              <IconHeadset size={28} stroke={1.5} color="white" />
            </Box>

            <Heading
              variant="h2"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 600,
                fontSize: { xs: '1.5rem', md: '1.75rem' },
                mb: 1,
              }}
            >
              Contact Support
            </Heading>

            <Text
              sx={{
                color: '#6c757d',
                fontSize: '0.95rem',
                mb: 0,
                lineHeight: 1.6,
              }}
            >
              Need help with your account? Click below to reach our support
              team.
            </Text>
          </Box>

          {/* Contact Support Button */}
          <SecureEmail
            emailKey="support"
            component="span"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              py: 1.75,
              px: 3,
              fontSize: '1rem',
              fontWeight: 600,
              fontFamily: '"Manrope", sans-serif',
              borderRadius: '9999px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 12px ${theme.palette.primary.main}4D`,
              },
            }}
          >
            Contact Support
          </SecureEmail>

          {/* Back to Sign In */}
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <MuiLink
              href="https://app.ellafin.ai"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                color: theme.palette.primary.main,
                textDecoration: 'none',
                fontSize: '0.95rem',
                fontWeight: 500,
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              <IconArrowLeft size={18} stroke={1.5} />
              Back to Sign In
            </MuiLink>
          </Box>

          {/* Footer Links */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              mt: 4,
              pt: 3,
              borderTop: '1px solid #e8e8e8',
              flexWrap: 'wrap',
            }}
          >
            <MuiLink
              component={Link}
              to="/privacy"
              sx={{
                color: theme.palette.grey[500],
                textDecoration: 'none',
                fontSize: '0.8rem',
                '&:hover': {
                  color: theme.palette.primary.main,
                },
              }}
            >
              Privacy Policy
            </MuiLink>
            <MuiLink
              component={Link}
              to="/terms"
              sx={{
                color: theme.palette.grey[500],
                textDecoration: 'none',
                fontSize: '0.8rem',
                '&:hover': {
                  color: theme.palette.primary.main,
                },
              }}
            >
              Terms of Service
            </MuiLink>
          </Box>

          {/* Copyright */}
          <Text
            sx={{
              textAlign: 'center',
              color: theme.palette.grey[400],
              fontSize: '0.75rem',
              mt: 2,
            }}
          >
            &copy; {new Date().getFullYear()} EllaFin&trade;, Inc. All rights
            reserved.
          </Text>
        </Box>
      </Box>
    </Box>
  );
}

export default ForgotPassword;
